import type {CopilotAssignmentRepository} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import {useCreateTask} from '@github-ui/agent-sessions/hooks/useCreateTask'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {createContext, memo, use, useCallback, useMemo, useState} from 'react'

import {repositoryWithOwnerFromString} from '../models/repository'
import {useRepositories} from '../state-providers/repositories/use-repositories'
import {Resources} from '../strings'
import useToasts, {ToastType} from './toasts/use-toasts'

const MEMEX_EVENT_TYPE = 'memex-project'

type StartCopilotPRCreator = (problemStatement?: string, repository?: CopilotAssignmentRepository) => Promise<void>

type CopilotPRCreatorContextValue = {
  start: StartCopilotPRCreator
  prefetch: () => void
}

const CopilotPRCreatorContext = createContext<CopilotPRCreatorContextValue | null>(null)

export const useStartCopilotPRCreator = () => use(CopilotPRCreatorContext)

interface CopilotPRCreatorProps {
  onClose: () => void
  repository: CopilotAssignmentRepository
  problemStatement: string
}

const CopilotPRCreator = ({onClose, repository, problemStatement}: CopilotPRCreatorProps) => {
  const {addToast} = useToasts()
  const {createTaskAsync} = useCreateTask({
    onError: error => {
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: ToastType.error,
        message:
          error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
            ? error.message
            : 'Failed to create Copilot pull request',
      })
    },
  })

  const handleSubmit = useCallback(
    async (
      _graphQLId: string,
      selectedBranch?: string,
      content?: string,
      selectedAgent?: SelectedAgent,
      selectedModel?: string,
    ) => {
      const nwo = `${repository.owner.login}/${repository.name}`
      const statement = content || problemStatement

      try {
        const _taskResult = await createTaskAsync({
          nwo,
          baseRef: selectedBranch,
          selectedAgent,
          problemStatement: statement,
          title: statement.slice(0, 100),
          eventType: MEMEX_EVENT_TYPE,
          modelId: selectedModel,
        })

        // TBD: Get PR info and add PR to the project board
        // Only do onClose() after that is complete

        onClose()
      } catch {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: ToastType.error,
          message: 'Failed to create Copilot pull request',
        })
      }
    },
    [repository, problemStatement, createTaskAsync, onClose, addToast],
  )

  return (
    <CopilotAssignmentModal
      isOpen
      onClose={onClose}
      repository={repository}
      onSubmit={handleSubmit}
      title={Resources.createCopilotPRTitle}
      description=""
      promptPlaceholder={Resources.createCopilotPRPromptPlaceholder}
      promptLabelHidden
      promptRequired
    />
  )
}

export const CopilotPRCreatorProvider = memo(function CopilotPRCreatorProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [creatorProps, setCreatorProps] = useState<CopilotPRCreatorProps | undefined>()
  const {suggestRepositories} = useRepositories()

  const closeModal = useCallback(() => {
    setCreatorProps(undefined)
  }, [])

  const startCopilotPRCreator = useCallback<StartCopilotPRCreator>(
    async (problemStatement = '', repository?: CopilotAssignmentRepository) => {
      let repo = repository
      if (!repo) {
        // Get the first suggested repository if none provided
        const repos = await suggestRepositories()
        const firstRepo = repos.at(0)
        if (firstRepo?.nameWithOwner) {
          const parsed = repositoryWithOwnerFromString(firstRepo.nameWithOwner)
          if (parsed) {
            repo = {
              name: parsed.name,
              owner: {login: parsed.owner},
            }
          }
        }
      }

      if (repo) {
        setCreatorProps({
          onClose: closeModal,
          repository: repo,
          problemStatement,
        })
      }
    },
    [suggestRepositories, closeModal],
  )

  const prefetch = useCallback(() => {
    // Prefetch repositories for faster modal opening
    void suggestRepositories()
  }, [suggestRepositories])

  const value: CopilotPRCreatorContextValue = useMemo(
    () => ({
      start: startCopilotPRCreator,
      prefetch,
    }),
    [startCopilotPRCreator, prefetch],
  )

  return (
    <CopilotPRCreatorContext value={value}>
      {children}
      {creatorProps && <CopilotPRCreator {...creatorProps} />}
    </CopilotPRCreatorContext>
  )
})

try{ CopilotPRCreatorContext.displayName ||= 'CopilotPRCreatorContext' } catch {}
try{ CopilotPRCreator.displayName ||= 'CopilotPRCreator' } catch {}
try{ CopilotPRCreatorProvider.displayName ||= 'CopilotPRCreatorProvider' } catch {}